import { Component, EventEmitter, forwardRef, HostListener, Input, OnInit, Output } from '@angular/core';
import { MyOverlayRef } from '../../overlay/myoverlay-ref';
import { DurationPickerOptions } from './duration-picker.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MaterialModule } from '../../../material-module';

@Component({
    selector: 'app-duration-picker',
    standalone : true,
    imports: [CommonModule, FormsModule, MaterialModule],
    templateUrl: './duration-picker.component.html',
    styleUrls: ['./duration-picker.component.scss']
})
export class DurationPickerComponent implements OnInit {
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: any) {
    this.keyDownEventManager(event);
  }

  @Input() set options(options: any) {
    this.attachChanges(this.validateOptions(options));
  }

  private _value: string = '';
  private _valueSeconds: number = 0;

  get value(): string {
    return this._value;
  }

  @Input()
  set value(value: string) {
    this._value = value;
    this.parse();
  }

  @Output() valueChange = new EventEmitter<string>();
  @Output() customOutput = new EventEmitter<string | number>();

  private _disabled = false;

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(disabled: boolean) {
    this._disabled = disabled;
  }

  regex: RegExp = /^[\+\-]?P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d+[HMS])(\d+H)?(\d+M)?(\d+S)?)?$/;

  private _negative = false;
  private _years = 0;
  private _months = 0;
  private _weeks = 0;
  private _days = 0;
  private _hours = 0;
  private _minutes = 0;
  private _seconds = 0;

  private overlayRef: MyOverlayRef;

  config: DurationPickerOptions = {
    showNegative: false,
    showButtons: false,
    showPreview: false,
    showLetters: false,
    showYears: true,
    showMonths: true,
    showWeeks: true,
    showDays: true,
    showHours: true,
    showMinutes: true,
    showSeconds: true,
    zeroValue: '',
    previewFormat: 'ISO',
    customOutputFormat: 'ISO',
    labels: {
      years: 'Y',
      months: 'M',
      weeks: 'W',
      days: 'D',
      hours: 'H',
      minutes: 'M',
      seconds: 'S',
    },
    unitSteps: {
      years: 1,
      months: 1,
      weeks: 1,
      days: 1,
      hours: 1,
      minutes: 1,
      seconds: 1,
    },
  };

  get preview(): string {
    if (!this.config.previewFormat || this.config.previewFormat.length === 0 || this.config.previewFormat === 'ISO') {
      return this.value;
    }
    return '';
  }

  get negative() {
    return this._negative;
  }
  set negative(value) {
    this._negative = value;
    this.emitNewValue();
  }

  get years() {
    return this._years;
  }

  set years(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._years = value;
    this.emitNewValue();
  }

  get months() {
    return this._months;
  }
  set months(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._months = value;
    this.emitNewValue();
  }

  get weeks() {
    return this._weeks;
  }
  set weeks(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._weeks = value;
    this.emitNewValue();
  }

  get days() {
    return this._days;
  }
  set days(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._days = value;
    this.emitNewValue();
  }

  get hours() {
    return this._hours;
  }
  set hours(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._hours = value;
    this.emitNewValue();
  }

  get minutes() {
    return this._minutes;
  }
  set minutes(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._minutes = value;
    this.emitNewValue();
  }

  get seconds() {
    return this._seconds;
  }
  set seconds(value) {
    value = this.parseNumber(value) > 0 ? value : 0;
    this._seconds = value;
    this.emitNewValue();
  }

  get valueInSeconds() {
    return (
      (this.negative ? -1 : 1) *
      (this.seconds +
        this.minutes * 60 +
        this.hours * 3600 +
        this.days * 86400 +
        this.weeks * 6048000 +
        this.months * 25920000 +
        this.years * 315360000)
    );
  }

  get valueInMilliseconds() {
    return this.valueInSeconds * 1000;
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(private ref: MyOverlayRef) {
    let data = this.ref.data;
    if (data != null) {
      this._valueSeconds = data;
      this._days = Math.floor(this._valueSeconds / (3600 * 24));
      this._hours = Math.floor((this._valueSeconds % (3600 * 24)) / 3600);
      this._minutes = Math.floor((this._valueSeconds % 3600) / 60);
      this._seconds = Math.floor(this._valueSeconds % 60);
      this.parse();
    }
    this.overlayRef = ref;
  }

  ngOnInit() {
    this.parse();
    this.value = this.generate();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    if (value) {
      this.value = value;
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  parse() {
    if (!this.valueInSeconds) {
      return;
    }

    this._days = Math.floor(this.valueInSeconds / (3600 * 24));
    this._hours = Math.floor((this.valueInSeconds % (3600 * 24)) / 3600);
    this._minutes = Math.floor((this.valueInSeconds % 3600) / 60);
    this._seconds = Math.floor(this.valueInSeconds % 60);
  }

  keyDownEventManager(event: any) {
    if (event.code == 'NumpadEnter' || event.code == 'Enter') {
      this.valider();
    }
  }

  parseNumber(value: any): number {
    return value ? parseInt(value, 10) : 0;
  }

  generate(): string {
    let output = '';

    if (this.config.showNegative && this.negative) {
      output = '-' + output;
    }

    if (this.config.showYears && this.years) {
      let yearStr = this.years > 1 ? 'années' : 'année';
      output += `${this.years}` + yearStr;
    }
    if (this.config.showMonths && this.months) {
      output += `${this.months} mois`;
    }
    if (this.config.showWeeks && this.weeks) {
      let weekStr = this.years > 1 ? 'semaines' : 'semaine';

      output += `${this.weeks} ` + weekStr;
    }
    if (this.config.showDays && this.days) {
      let dayStr = this.years > 1 ? 'jours' : 'jour';

      output += `${this.days} ` + dayStr;
    }
    if ((this.config.showHours && this.hours) || (this.config.showMinutes && this.minutes) || (this.config.showSeconds && this.seconds)) {
      output += ', ';

      if (this.config.showHours && this.hours) {
        let hoursStr = this.years > 1 ? 'heures' : 'heure';

        output += `${this.hours} ` + hoursStr;
      }
      if (this.config.showMinutes && this.minutes) {
        let minutesStr = this.minutes > 1 ? 'minutes' : 'minute';

        output += ` ${this.minutes} ` + minutesStr;
      }
      if (this.config.showSeconds && this.seconds) {
        let secStr = this.seconds > 1 ? 'secondes' : 'seconde';
        output += ` ${this.seconds} ` + secStr;
      }
    }
    if (output.charAt(0) == ',') output = output.substring(1);

    return output;
  }

  emitNewValue() {
    this.value = this.generate();
    // this.valueChange.emit();
    this.parse();
    this.onTouched();
    this.onChange(this.value);
  }

  // Attach all the changes received in the options object
  attachChanges(options: any): void {
    Object.keys(options).forEach((param) => {
      if (this.config.hasOwnProperty(param)) {
        if (typeof this.config[param as keyof DurationPickerOptions] === 'object') {
          this.config[param as keyof DurationPickerOptions] = {
            ...(this.config[param as keyof DurationPickerOptions] as object),
            ...options[param],
          };
        } else {
          this.config[param as keyof DurationPickerOptions] = options[param];
        }
      }
    });
  }

  validateOptions(options: any): any {
    const validatedOptions: any = { ...options };
    if (typeof options.unitSteps === 'object' && Object.values(options.unitSteps).some((unitStep) => (unitStep as number) <= 0)) {
      console.warn('Use positive values for unit steps');
      validatedOptions.unitSteps = Object.keys(options.unitSteps).reduce((steps: any, unit) => {
        steps[unit] = options.unitSteps[unit] > 0 ? options.unitSteps[unit] : 1;
        return steps;
      }, {});
    }

    return validatedOptions;
  }

  cancel() {
    this.overlayRef.close(null);
  }

  valider() {
    this.overlayRef.close(this.valueInSeconds);
  }
}
